/**
 * Auto-Campaign — Phase 2 request schemas.
 *
 * Range constraints mirror the env-var ranges defined in
 * `apps/workers/src/auto-campaign/config.ts` (which is the source of truth for
 * defaults). Duplicating the ranges here keeps the API-side isolation guarantee
 * intact — the API never reads `AUTO_CAMPAIGN_*` env vars, so range
 * validation has to live on this side of the boundary too.
 *
 * Only override fields are represented — the API never accepts nor returns the
 * full env-level config through these endpoints.
 */

import { z } from 'zod';

// -----------------------------------------------------------------------------
// PATCH /v1/projects/:id/auto-campaign/config — per-project override upsert
// -----------------------------------------------------------------------------

/**
 * Whitelist of A10 platform codes accepted by the config override. Matches the
 * CSV values used by AUTO_CAMPAIGN_OFFPAGE_PLATFORMS. Kept liberal (short
 * codes, not the full A10 platform enum) because these are the codes users see
 * in the enrollment UI. Unknown codes are rejected up-front so the worker
 * doesn't silently drop them at dispatch time.
 */
export const AutoCampaignOffpagePlatformCodeSchema = z.enum([
  'linkedin',
  'quora',
  'reddit',
  'medium',
  'devto',
]);
export type AutoCampaignOffpagePlatformCode = z.infer<
  typeof AutoCampaignOffpagePlatformCodeSchema
>;

/**
 * IANA timezone — validated best-effort by attempting to construct an
 * `Intl.DateTimeFormat` with the value. Rejects nonsense strings; accepts any
 * region the JS runtime knows about.
 */
const TimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Invalid IANA timezone' },
  );

/**
 * Basic cron expression sanity check — 5 fields separated by whitespace.
 * The workers-side loader does the deep validation with `cron-parser`; this
 * gate just rejects clearly-malformed input at the API surface.
 */
const CronSchema = z
  .string()
  .trim()
  .min(9)
  .max(120)
  .refine(
    (v) => v.split(/\s+/).length === 5,
    { message: 'Cron expression must have 5 whitespace-separated fields' },
  );

export const AutoCampaignConfigOverrideSchema = z
  .object({
    briefs_per_fire: z.number().int().min(1).max(15).optional(),
    variants_per_platform: z.number().int().min(1).max(5).optional(),
    offpage_platforms: z
      .array(AutoCampaignOffpagePlatformCodeSchema)
      .max(10)
      .optional(),
    llm_token_cap_per_fire: z.number().int().min(100_000).max(50_000_000).optional(),
    pdf_attach_max: z.number().int().min(0).max(30).optional(),
    force_full_run: z.boolean().optional(),
    schedule_exec_cron: CronSchema.optional(),
    schedule_delivery_cron: CronSchema.optional(),
    timezone: TimezoneSchema.optional(),
  })
  .strict();

export type AutoCampaignConfigOverride = z.infer<
  typeof AutoCampaignConfigOverrideSchema
>;

// -----------------------------------------------------------------------------
// POST /v1/projects/:id/auto-campaign/run-now — per-fire overrides
// -----------------------------------------------------------------------------

export const AutoCampaignRunNowSchema = z
  .object({
    briefs_per_fire: z.number().int().min(1).max(15).optional(),
    force_full_run: z.boolean().optional(),
  })
  .strict();

export type AutoCampaignRunNowRequest = z.infer<typeof AutoCampaignRunNowSchema>;

// -----------------------------------------------------------------------------
// POST /v1/projects/:id/auto-campaign/invite-link
// -----------------------------------------------------------------------------

export const AutoCampaignInviteLinkSchema = z
  .object({
    // 1 hour default, 24 hour max. Beyond a day the link starts to become a
    // security concern (long-lived pre-authenticated join link).
    ttl_seconds: z.number().int().min(60).max(86_400).optional(),
  })
  .strict();

export type AutoCampaignInviteLinkRequest = z.infer<
  typeof AutoCampaignInviteLinkSchema
>;

// -----------------------------------------------------------------------------
// Hardcoded API-side defaults
// -----------------------------------------------------------------------------

/**
 * API-side defaults for the effective-config response. These mirror the
 * `apps/workers/src/auto-campaign/config.ts` defaults but MUST NOT read from
 * env — the isolation script (`scripts/check-auto-campaign-isolation.sh`)
 * forbids auto-campaign env access from the API package. Any drift between
 * the two default sets is intentional: workers see per-deployment env
 * overrides, the API always shows the hardcoded fallback when no project row
 * exists (which the UI labels as "using defaults").
 */
export const AUTO_CAMPAIGN_API_DEFAULTS = {
  briefs_per_fire: 3,
  variants_per_platform: 1,
  offpage_platforms: ['linkedin', 'quora', 'reddit', 'medium'] as const,
  llm_token_cap_per_fire: 5_000_000,
  pdf_attach_max: 20,
  force_full_run: false,
  schedule_exec_cron: '0 22 * * 0,3',
  schedule_delivery_cron: '0 11 * * 1,4',
  timezone: 'Asia/Kolkata',
} as const;

export interface EffectiveAutoCampaignConfig {
  briefs_per_fire: number;
  variants_per_platform: number;
  offpage_platforms: string[];
  llm_token_cap_per_fire: number;
  pdf_attach_max: number;
  force_full_run: boolean;
  schedule_exec_cron: string;
  schedule_delivery_cron: string;
  timezone: string;
}

// -----------------------------------------------------------------------------
// Canonical auto-campaign DAG — shared between the scheduled tick (workers)
// and the Run-Now endpoint (API) so both fire paths produce IDENTICAL plans.
// Pure data builders: no env access, no DB. Payload values mirror the manual
// endpoints' production defaults (POST /v1/audits → 200/5 crawl,
// POST /v1/keywords → smart-seed with audit reuse).
// -----------------------------------------------------------------------------

import type { GoalParseOutput, StrategyDecomposeOutput } from './campaign.js';

export interface CannedDagOptions {
  /** Skip the a1_audit step (cadence: audit fresher than A1_SKIP_DAYS). */
  skipA1?: boolean;
  /** Skip the a2_keyword step (cadence: keyword run fresher than A2_SKIP_DAYS). */
  skipA2?: boolean;
}

export function buildCannedAutoCampaignParsedGoal(): GoalParseOutput {
  return {
    metric: 'organic_traffic',
    baseline_window_days: 90,
    target_delta_pct: 25,
    target_scope: 'whole site',
    time_horizon_days: 90,
    rationale:
      'Auto-campaign canonical target: continuous organic growth via audit + keyword + competitor + brief loop.',
  };
}

/**
 * Build the canonical 7-step auto-campaign DAG (minus cadence-skipped steps).
 *
 * Shape:
 *   precheck → a1_audit → a2_keyword → { a5_competitor, a7_rank } →
 *   a3_brief_batch → a10_off_page_batch
 *
 * When a1/a2 are cadence-skipped, downstream deps re-anchor on the closest
 * surviving upstream step.
 */
export function buildCannedAutoCampaignStrategy(
  projectDomain: string,
  opts: CannedDagOptions = {},
): StrategyDecomposeOutput {
  const rootUrl = projectDomain.startsWith('http')
    ? projectDomain
    : `https://${projectDomain}`;

  type Step = StrategyDecomposeOutput['steps'][number];
  const steps: Step[] = [];
  const seqOf: Record<string, number> = {};
  const add = (name: string, step: Omit<Step, 'depends_on_seq'>, deps: string[]): void => {
    seqOf[name] = steps.length;
    steps.push({
      ...step,
      depends_on_seq: deps
        .filter((d) => seqOf[d] !== undefined)
        .map((d) => seqOf[d]!),
    });
  };

  add('precheck', {
    step_type: 'precheck',
    title: 'Validate project sanity',
    rationale: 'Ensure domain resolves + country/lang set before spending on crawl.',
    payload: {},
  }, []);

  if (!opts.skipA1) {
    add('a1', {
      step_type: 'a1_audit',
      title: 'Full site audit',
      rationale: 'Baseline health + technical inventory + PSI + GSC.',
      // Manual defaults from apps/api/src/usecases/create-audit.ts.
      payload: { max_pages: 200, max_depth: 5 },
    }, ['precheck']);
  }

  if (!opts.skipA2) {
    add('a2', {
      step_type: 'a2_keyword',
      title: 'Keyword research (smart-seed)',
      rationale: 'Discover clusters + gap opportunities from audit inventory.',
      // Manual defaults from apps/api/src/usecases/create-keyword-run.ts.
      payload: {
        seed: { type: 'domain', value: rootUrl },
        smartMode: true,
        reuseAudit: true,
      },
    }, ['a1', 'precheck']);
  }

  // Downstream anchor: the freshest surviving research step.
  const anchor = !opts.skipA2 ? 'a2' : !opts.skipA1 ? 'a1' : 'precheck';

  add('a5', {
    step_type: 'a5_competitor',
    title: 'Competitor scan',
    rationale: 'Sitemap + rank + backlink deltas vs tracked competitors.',
    payload: {},
  }, [anchor]);

  add('a7', {
    step_type: 'a7_rank',
    title: 'Rank tracking snapshot',
    rationale: 'Baseline SERP positions for tracked keywords.',
    payload: {},
  }, [anchor]);

  add('a3batch', {
    step_type: 'a3_brief_batch',
    title: 'Content brief batch',
    rationale: 'Top gap clusters → concrete briefs + off-page fanout children.',
    payload: {},
  }, [anchor, 'a5']);

  add('a10batch', {
    step_type: 'a10_off_page_batch',
    title: 'Off-page distribution batch',
    rationale: 'Safety net — a3_brief_batch already inserts the a10 children.',
    payload: {},
  }, ['a3batch']);

  return {
    archetype: 'mixed',
    rationale:
      'Canonical auto-campaign DAG: audit → keyword research → competitor scan + rank tracking → briefs → off-page fanout.',
    key_assumptions: [
      'Fresh audit + keyword baseline drives brief selection.',
      'Off-page briefs fan out from newly-created briefs, not stale ones.',
    ],
    steps,
  };
}
